import { useMemo, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ConnectProto } from "@racoon/api";
import { compareIds } from "~/shared/lib/compare-ids";
import { scheduleLive, schedulesByCgId, todayKey } from "~/shared/lib/container-group-schedule";
import { unpricedContainerGroups } from "~/shared/lib/container-group-job-types";
import { useListContainerGroupSchedulesQuery } from "./container-group-schedule.grpc";

type Field = "pickupLocationIds" | "containerGroupIds";
const NO_SCHEDULES: ConnectProto.ContainerGroupSchedule[] = [];

/** Distinct ids in id order, joined: a new array of the same ids keeps the key. */
const idsKey = (ids: (bigint | string)[]) =>
  [...new Set(ids.map(String))].sort(compareIds).join(",");

/** The container groups of the locations an answer did not cover. */
const unknownGroups = (locations: ConnectProto.PickupLocation[], answered: Set<string>) =>
  new Set(
    locations
      .filter((l) => !answered.has(String(l.id)))
      .flatMap((l) => (l.containerGroups ?? []).map((cg) => String(cg.id))),
  );

/**
 * One ListContainerGroupSchedules over the ids; the backend narrows it to the groups the caller may
 * view, at a location whoever owns them (go-backend #575), so a foreign id no longer fails the list.
 * `data` outlives a refetch (a new page, a schedule write), so known rows stay; ids the last answer
 * did not cover read as unknown until the new one arrives.
 */
const useSchedules = (field: Field, key: string) => {
  const arg = useMemo(
    () => (key ? { [field]: key.split(",").map((id) => BigInt(id)) } : skipToken),
    [field, key],
  );
  const { data, currentData, isError, isFetching } = useListContainerGroupSchedulesQuery(arg);
  // The ids the last answer was for.
  const answered = useRef("");
  if (currentData) answered.current = key;
  return {
    schedules: data?.schedules ?? NO_SCHEDULES,
    answeredKey: answered.current,
    loaded: !key || !!currentData || isError,
    failed: isError,
    isFetching,
  };
};

/**
 * Schedule rows of the given pickup locations; groups of a location the last answer did not cover
 * are unknown.
 */
export const useLocationSchedules = (locations: ConnectProto.PickupLocation[], skip = false) => {
  const key = skip ? "" : idsKey(locations.map((l) => l.id));
  const { schedules, answeredKey, loaded, failed, isFetching } = useSchedules(
    "pickupLocationIds",
    key,
  );
  return useMemo(() => {
    const unknownCgIds = unknownGroups(locations, new Set(answeredKey.split(",")));
    return { schedules, unknownCgIds, loaded, failed, isFetching };
  }, [schedules, answeredKey, locations, loaded, failed, isFetching]);
};

/**
 * Schedule rows still in effect of the given container groups, by stringified id; groups the last
 * answer did not cover are in `unknownCgIds`. The panel and the day filter ask for the same ids, so
 * they share the cache entry.
 */
export const useContainerGroupSchedules = (
  containerGroups: Pick<ConnectProto.ContainerGroup, "id">[] | undefined,
  skip = false,
) => {
  const key = skip ? "" : idsKey((containerGroups ?? []).map((cg) => cg.id));
  const { schedules, answeredKey, loaded, failed, isFetching } = useSchedules(
    "containerGroupIds",
    key,
  );
  // As in the reports: an ended schedule neither counts as one nor offers its job type.
  const today = todayKey();
  return useMemo(() => {
    const answered = new Set(answeredKey.split(","));
    return {
      schedulesByCgId: schedulesByCgId(schedules.filter((s) => scheduleLive(s, today))),
      unknownCgIds: new Set((key ? key.split(",") : []).filter((id) => !answered.has(id))),
      loaded,
      failed,
      isFetching,
    };
  }, [schedules, answeredKey, key, today, loaded, failed, isFetching]);
};

/**
 * Schedules of the groups whose contract prices no job type, the only ones that fall back on them,
 * and the hint to show while they load or after they fail.
 */
export const useUnpricedGroupSchedules = (groups: ConnectProto.ContainerGroup[], skip = false) => {
  const unpriced = useMemo(() => unpricedContainerGroups(groups), [groups]);
  const { schedulesByCgId, isFetching, failed } = useContainerGroupSchedules(unpriced, skip);
  const hintKey = failed
    ? ("groups.bindModal.schedulesFailed" as const)
    : isFetching
      ? ("groups.bindModal.schedulesLoading" as const)
      : null;
  return { schedulesByCgId, hintKey };
};

/** The same request fetched once, e.g. for an export; a failure leaves every group unknown. */
export const fetchLocationSchedules = async (
  fetch: (arg: { pickupLocationIds: bigint[] }) => Promise<ConnectProto.ContainerGroupScheduleList>,
  locations: ConnectProto.PickupLocation[],
) => {
  const key = idsKey(locations.map((l) => l.id));
  const answer = key
    ? await fetch({ pickupLocationIds: key.split(",").map((id) => BigInt(id)) }).catch(
        () => undefined,
      )
    : { schedules: [] };
  return {
    schedules: answer?.schedules ?? [],
    unknownCgIds: unknownGroups(locations, new Set(answer ? key.split(",") : [])),
  };
};
